import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { apkLabelClass, useApks } from '@/lib/api/apks';
import { Box, brandFamily } from '@/lib/api/boxes';
import { useLiveIps } from '@/lib/api/liveBoxes';
import { createTask, TaskCommand } from '@/lib/api/task';

/** Picks which APK to install on the boxes already selected in the table.
 *  Brand safety lives here: only selected boxes whose brand matches the APK's
 *  tag are sent (and the agent re-checks before running adb install). */
export function InstallApkDialog({
  open,
  onOpenChange,
  boxes,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The boxes selected in the table when the dialog was opened. */
  boxes: Box[];
}) {
  const navigate = useNavigate();
  const [apks] = useApks();
  const liveIps = useLiveIps();
  const [apkId, setApkId] = useState('');
  const [installing, setInstalling] = useState(false);

  // Fresh pick each time the dialog opens.
  useEffect(() => {
    if (open) setApkId('');
  }, [open]);

  const apk = useMemo(
    () => apks?.find((a) => a.id === apkId) ?? null,
    [apks, apkId]
  );
  // Family match: S905/928X run the NVIDIA build (NEXUS-vs-others rule).
  const matching = apk
    ? boxes.filter((b) => brandFamily(b.brand) === apk.brand)
    : [];
  const skipped = apk
    ? boxes.filter((b) => brandFamily(b.brand) !== apk.brand)
    : [];
  const targets = matching.filter((b) => !liveIps.has(b.ip));
  const wrongBrand = apk ? boxes.length - matching.length : 0;
  const inLiveView = matching.length - targets.length;

  const onInstall = async () => {
    if (!apk || targets.length === 0) return;
    setInstalling(true);
    try {
      const id = await createTask({
        command: TaskCommand.InstallApk,
        boxes: targets.map((b) => b.ip),
        isTvControllerTask: false,
        data: {
          storagePath: apk.storagePath,
          apkName: apk.name,
          brand: apk.brand,
        },
      });
      onOpenChange(false);
      navigate('/boxes/task/' + id);
    } catch (error) {
      console.error('install task failed', error);
      toast.error('Failed to start the install task');
    } finally {
      setInstalling(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Install APK</DialogTitle>
          <DialogDescription>
            {boxes.length} box{boxes.length === 1 ? '' : 'es'} selected. The APK
            only installs on boxes matching its type.
          </DialogDescription>
        </DialogHeader>

        {/* min-w-0 at every level: the dialog is a grid and the trigger is a
            flex row — both default to min-width auto, so a long unbreakable
            APK name pushed the select wider than the dialog. */}
        <div className="grid min-w-0 gap-2 py-2">
          <label className="text-sm font-medium">APK</label>
          <Select value={apkId} onValueChange={setApkId}>
            <SelectTrigger className="min-w-0 overflow-hidden [&>span]:min-w-0 [&>span]:flex-1">
              <SelectValue placeholder="Select an APK" />
            </SelectTrigger>
            <SelectContent>
              {apks?.length ? (
                apks.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {/* min-w-0 + truncate so a long build name ellipsizes on
                        one line instead of wrapping under the badges. */}
                    <span className="flex w-full min-w-0 items-center gap-2">
                      <span className="min-w-0 flex-1 truncate text-left">
                        {a.name}
                      </span>
                      {a.label && (
                        <Badge
                          variant="outline"
                          className={`shrink-0 whitespace-nowrap ${apkLabelClass(a.label)}`}
                        >
                          {a.label}
                        </Badge>
                      )}
                      <Badge
                        variant={a.brand === 'NVIDIA' ? 'default' : 'secondary'}
                        className="shrink-0"
                      >
                        {a.brand}
                      </Badge>
                    </span>
                  </SelectItem>
                ))
              ) : (
                <SelectItem value="none" disabled>
                  No APKs uploaded
                </SelectItem>
              )}
            </SelectContent>
          </Select>

          {apk && (
            <div className="space-y-1 pt-2 text-sm">
              <p>
                Will install on <strong>{targets.length}</strong> of the{' '}
                {boxes.length} selected box{boxes.length === 1 ? '' : 'es'}.
              </p>
              {wrongBrand > 0 && (
                <div className="text-muted-foreground">
                  <p>
                    {wrongBrand} skipped — not the {apk.brand} family:
                  </p>
                  <ul className="mt-0.5 list-inside list-disc">
                    {skipped.slice(0, 5).map((b) => (
                      <li key={b.ip}>
                        {b.name} <span className="text-xs">({b.brand})</span>
                      </li>
                    ))}
                    {skipped.length > 5 && <li>+{skipped.length - 5} more</li>}
                  </ul>
                </div>
              )}
              {inLiveView > 0 && (
                <p className="text-muted-foreground">
                  {inLiveView} skipped — in live view.
                </p>
              )}
              {targets.length === 0 && (
                <p className="text-destructive">
                  None of the selected boxes can take this APK.
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={installing}
          >
            Cancel
          </Button>
          <Button
            onClick={onInstall}
            disabled={installing || !apk || targets.length === 0}
          >
            {installing
              ? 'Starting…'
              : `Install on ${targets.length} box${targets.length === 1 ? '' : 'es'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
